/**
 * The Inventory tab: items owned but not yet used. Today that is the two
 * grades of Gene Splice, bought in the Meteor Shop.
 *
 * Using one is a two-step choice, so nothing is spent by accident: click the
 * item to arm it, then click a dinosaur — a row in the list or one in the
 * yard. While an item is armed the shop rows say what each dinosaur would
 * become, or why it cannot be used, the holding bar (ui/holding.ts) says what
 * is held on every tab, and the pointer is the item.
 */

import { ITEMS } from '../data/items';
import type { ItemDef } from '../data/items';
import type { DinoDef } from '../data/dinos';
import { countOf, eligibility, eligibleTargets, heldItems } from '../game/inventory';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';

export interface InventoryView {
  update(state: GameState): void;
  /** The item waiting for a target, or null. */
  readonly armed: ItemDef | null;
  /** Puts an item down without using it. */
  disarm(): void;
}

export interface InventoryHooks {
  /** The player armed an item, or disarmed (null). The shop highlights targets. */
  onArm(item: ItemDef | null): void;
}

/** Each item's 48px icon in public/art/icons/items/, named after the id's second half. */
export function itemIconUrlById(itemId: string): string {
  return `${import.meta.env.BASE_URL}art/icons/items/${itemId.split(':')[1]}.png`;
}

export function itemIconUrl(def: ItemDef): string {
  return itemIconUrlById(def.id);
}

/** The 32 px copy, which is the pointer while the item is held. */
export function itemCursorUrl(def: ItemDef): string {
  return `${import.meta.env.BASE_URL}art/icons/items/${def.id.split(':')[1]}-32.png`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

interface Slot {
  def: ItemDef;
  button: HTMLButtonElement;
  count: HTMLElement;
  lastCount: number;
  lastArmed: boolean;
}

export function createInventoryView(root: HTMLElement, hooks: InventoryHooks): InventoryView {
  const empty = el(
    'p',
    'upgrades__empty',
    'Nothing here right now. Splices bought in the Shop wait here until you choose who gets one.',
  );
  const list = el('div', 'inventory__slots');
  root.replaceChildren(empty, list);

  let armed: ItemDef | null = null;
  let viewState: GameState | null = null;

  const slots: Slot[] = ITEMS.map((def) => {
    const button = el('button', 'inventory__slot');
    button.type = 'button';
    button.hidden = true;
    const img = document.createElement('img');
    img.className = 'inventory__icon';
    img.alt = '';
    img.src = itemIconUrl(def);
    const count = el('span', 'inventory__count', '0');
    const name = el('span', 'inventory__name', def.name);
    button.append(img, count, name);
    bindTooltip(button, () => {
      const state = viewState;
      const targets = state ? eligibleTargets(def, state) : [];
      return {
        icon: { src: itemIconUrl(def) },
        lines: [
          { text: def.name },
          { text: def.description, muted: true },
          {
            text:
              targets.length > 0
                ? `Can be used on ${targets.length} dinosaur${targets.length === 1 ? '' : 's'}`
                : 'No dinosaur is ready for this yet',
            muted: true,
          },
        ],
      };
    });
    button.addEventListener('click', () => arm(armed === def ? null : def));
    list.append(button);
    return { def, button, count, lastCount: -1, lastArmed: false };
  });

  function arm(item: ItemDef | null): void {
    armed = item;
    for (const slot of slots) {
      const on = slot.def === armed;
      if (on !== slot.lastArmed) {
        slot.lastArmed = on;
        slot.button.classList.toggle('inventory__slot--armed', on);
      }
    }
    hooks.onArm(armed);
  }

  return {
    get armed() {
      return armed;
    },
    disarm() {
      if (armed) arm(null);
    },
    update(state) {
      viewState = state;
      const held = heldItems(state);
      let any = false;
      for (const slot of slots) {
        const count = countOf(state, slot.def.id);
        any ||= count > 0;
        if (count !== slot.lastCount) {
          slot.lastCount = count;
          slot.button.hidden = count === 0;
          slot.count.textContent = `x${count}`;
          slot.button.setAttribute(
            'aria-label',
            `${slot.def.name}, ${count} held: ${slot.def.description}`,
          );
        }
      }
      // An item used up while armed puts itself down.
      if (armed && countOf(state, armed.id) === 0) arm(null);
      empty.hidden = held.length > 0;
    },
  };
}

/** Why an armed item cannot be used on this dinosaur, or null when it can. */
export function blockedReason(item: ItemDef, def: DinoDef, state: GameState): string | null {
  const e = eligibility(item, def, state);
  return e.ok ? null : e.reason;
}
